/**
 * Oudh Al Anfar — Watch & Buy
 * ---------------------------------------------------------------------------
 * A rail of vertical 9:16 videos, each tagged to a product.
 *
 * Performance is the whole design. Nothing downloads until a card is within one
 * viewport of the fold, only the card nearest the centre plays, and every other
 * card is paused. On a slow connection or with `prefers-reduced-motion` nothing
 * autoplays at all — the poster stays and the shopper taps to start, which is
 * also the only behaviour iOS low-power mode will honour.
 *
 * AUTOPLAY GATING — two conditions, both required:
 *   1. the SECTION is in the viewport   (observer with root: null)
 *   2. the CARD is centred in the rail  (observer with root: the track)
 * An earlier version only checked (2). Because that observer's root was the
 * rail, a card sitting in the middle of the rail counted as intersecting even
 * while the whole section was scrolled far off-screen — so videos played, and
 * burned bandwidth, where nobody could see them.
 *
 * SOUND is off until the shopper asks for it, because muted is the only state
 * a browser will autoplay. Once they turn it on, the preference follows the
 * rail: whichever card is playing carries the sound, and the rest stay muted.
 * If an unmuted play() is refused we fall back to muted rather than leaving a
 * dead frame.
 *
 * THE ARROWS. The section has always rendered prev/next buttons and nothing
 * ever bound them — they were decorative. They now scroll the rail by exactly
 * one card, disable at each end, and hide themselves when everything fits.
 * ---------------------------------------------------------------------------
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const CART_ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';
const CART_URL = window.Theme?.routes?.cart_url || '/cart';

/** Fraction of a card that must sit inside the rail for it to count as centred. */
const CENTRED_RATIO = 0.6;
/** Fraction of the section that must be on screen before anything may play. */
const SECTION_RATIO = 0.15;

class OaaWatchBuy extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-wb-card]')];
    this.track = this.querySelector('[data-rail-track]');
    if (!this.cards.length) return;

    // The arrows live in the section head, outside this element.
    this.section = this.closest('.oaa-watch');
    this.nav = this.section?.querySelector('[data-rail-nav]');
    this.prev = this.section?.querySelector('[data-rail-prev]');
    this.next = this.section?.querySelector('[data-rail-next]');

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.saveData = navigator.connection?.saveData === true;

    /** Cards currently centred in the rail. */
    this.centred = new Set();
    /** True while the section itself is on screen. */
    this.inView = false;
    /** Sound is opt-in. */
    this.soundOn = false;

    this.#hydrate();
    this.#watch();
    this.#bindRail();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#loadObserver?.disconnect();
    this.#playObserver?.disconnect();
    this.#sectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    cancelAnimationFrame(this.#raf);
  }

  #loadObserver;
  #playObserver;
  #sectionObserver;
  #resizeObserver;
  #raf = 0;

  /* ---------------------------------------------------------------- rail */

  #bindRail() {
    if (!this.track || !this.nav) return;

    const step = () => {
      const first = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      return first ? first.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    };

    const overflows = () => this.track.scrollWidth - this.track.clientWidth > 2;

    const sync = () => {
      if (!overflows()) {
        this.nav.setAttribute('hidden', '');
        return;
      }
      this.nav.removeAttribute('hidden');
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      // Works in both directions — scrollLeft goes negative in RTL.
      this.prev?.toggleAttribute('disabled', Math.abs(x) <= 2);
      this.next?.toggleAttribute('disabled', Math.abs(Math.abs(x) - max) <= 2);
    };

    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step() * dir, behavior: 'smooth' }));
    this.next?.addEventListener('click', () => this.track.scrollBy({ left: step() * dir, behavior: 'smooth' }));

    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(sync);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(sync);
      this.#resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', sync, { passive: true });
    }

    sync();
  }

  /* ------------------------------------------------------------- loading */

  /** Attaches sources only once a card is close to the viewport. */
  #hydrate() {
    this.#loadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#attachSources(entry.target);
          this.#loadObserver.unobserve(entry.target);
        }
      },
      { rootMargin: '100% 0px' }
    );

    this.cards.forEach((card) => this.#loadObserver.observe(card));
  }

  /** Swaps data-src onto src. Safe to call more than once. */
  #attachSources(card) {
    const video = card.querySelector('video');
    if (!video || video.dataset.hydrated) return video ?? null;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    video.load();
    video.dataset.hydrated = 'true';
    return video;
  }

  /* ------------------------------------------------------------ playback */

  #watch() {
    if (this.reducedMotion || this.saveData) return;

    // (1) Is the section on screen at all? Root is the viewport.
    this.#sectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.inView = entry.isIntersecting && entry.intersectionRatio >= SECTION_RATIO;
        }
        this.#sync();
      },
      { threshold: [0, SECTION_RATIO, 0.5] }
    );
    this.#sectionObserver.observe(this);

    // (2) Which card is centred? Root is the horizontal rail.
    this.#playObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio > CENTRED_RATIO) {
            this.centred.add(entry.target);
          } else {
            this.centred.delete(entry.target);
            delete entry.target.dataset.wbManual;
          }
        }
        this.#sync();
      },
      { threshold: [0, CENTRED_RATIO, 1], root: this.track ?? null }
    );

    this.cards.forEach((card) => this.#playObserver.observe(card));
  }

  /** The single place playback is decided. */
  #sync = () => {
    const allowed = this.inView && document.visibilityState === 'visible';

    for (const card of this.cards) {
      const video = card.querySelector('video');
      if (!video) continue;

      const manual = card.dataset.wbManual === 'true';
      const shouldPlay = allowed && (manual || this.centred.has(card));

      if (shouldPlay) {
        if (!video.dataset.hydrated) this.#attachSources(card);
        if (video.paused) this.#play(video);
      } else if (!video.paused) {
        video.pause();
        video.muted = true;
      }

      this.#paint(card, video);
    }
  };

  /** Plays with sound if the shopper asked for it, muted if the browser refuses. */
  #play(video) {
    video.muted = !this.soundOn;
    const attempt = video.play();
    if (attempt?.catch) {
      attempt.catch(() => {
        if (video.muted) return; // Nothing else to try — poster and controls remain.
        video.muted = true;
        video.play().catch(() => {});
        this.#paintAll();
      });
    }
  }

  /** Keeps the two control buttons telling the truth. */
  #paint(card, video) {
    card.querySelector('[data-wb-toggle]')?.setAttribute('aria-pressed', String(!video.paused));
    const sound = card.querySelector('[data-wb-sound]');
    if (sound) {
      sound.setAttribute('aria-pressed', String(!video.muted));
      sound.setAttribute('aria-label', video.muted ? sound.dataset.labelOn || 'Turn sound on' : sound.dataset.labelOff || 'Turn sound off');
    }
  }

  #paintAll() {
    for (const card of this.cards) {
      const video = card.querySelector('video');
      if (video) this.#paint(card, video);
    }
  }

  #onVisibility = () => this.#sync();

  /* -------------------------------------------------------------- clicks */

  #onClick = (event) => {
    const sound = event.target.closest('[data-wb-sound]');
    if (sound) {
      event.preventDefault();
      const card = sound.closest('[data-wb-card]');
      const video = card?.querySelector('video');
      if (!video) return;

      this.soundOn = video.muted;

      // One card at a time carries the sound.
      for (const other of this.cards) {
        const otherVideo = other.querySelector('video');
        if (otherVideo && otherVideo !== video) otherVideo.muted = true;
      }

      video.muted = !this.soundOn;
      if (this.soundOn && video.paused) {
        card.dataset.wbManual = 'true';
        this.#play(video);
      }
      this.#paintAll();
      return;
    }

    const toggle = event.target.closest('[data-wb-toggle]');
    if (!toggle) return;

    const card = toggle.closest('[data-wb-card]');
    const video = card?.querySelector('video');
    if (!video) return;

    if (video.paused) {
      this.#attachSources(card);
      card.dataset.wbManual = 'true';
      this.#play(video);
    } else {
      delete card.dataset.wbManual;
      video.pause();
    }
    this.#paint(card, video);
  };

  /* ---------------------------------------------------------------- cart */

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.oaa-watch__form')) return;
    event.preventDefault();

    const variantId = new FormData(form).get('id');
    const button = form.querySelector('button[type="submit"]');
    const status = this.querySelector('[data-wb-status]');
    const title = form.closest('[data-wb-card]')?.querySelector('.oaa-watch__tag-title')?.textContent?.trim() || '';
    if (!variantId) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
      .filter(Boolean);
    const eventPromise = CartLinesUpdateEvent.createPromise();

    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: eventPromise.promise,
    }));

    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    try {
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Add failed');

      const cartResponse = await fetch(`${CART_URL}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();

      eventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'oaa-watch-buy',
          sourceId: form.id,
          itemCount: 1,
          sections: result.sections,
          didError: false,
        },
      });
      if (status) status.textContent = (this.dataset.added || 'Added __TITLE__').replace('__TITLE__', title);
    } catch (error) {
      eventPromise.reject(error);
      if (status) status.textContent = this.dataset.error || 'Could not add this product.';
    } finally {
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };
}

if (!customElements.get('oaa-watch-buy')) {
  customElements.define('oaa-watch-buy', OaaWatchBuy);
}